import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X } from "lucide-react";

/** A banner past its end is simply not shown; nothing rewrites the record. */
const stillRunning = (b: { expires_at?: string | null }) =>
  !b.expires_at || new Date(b.expires_at).getTime() > Date.now();

interface NotificationBannerData {
  id: string;
  message: string;
  expires_at?: string | null;
  is_active: boolean;
  background_color: string;
  text_color: string;
}

const NotificationBanner = () => {
  const [banner, setBanner] = useState<NotificationBannerData | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchBanner();

    // Subscribe to realtime updates with error handling
    const channel = supabase
      .channel('notification-banner-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_banner'
        },
        () => {
          fetchBanner();
          setIsDismissed(false); // Show banner again when it's updated
        }
      )
      .subscribe((status, err) => {
        // Silently handle connection errors to prevent console pollution
        // The banner still works via initial fetch
        if (status === 'CHANNEL_ERROR' || err) {
          // Connection failed - this is expected if realtime isn't available
          // Component still functions normally with initial data fetch
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchBanner = async () => {
    const { data } = await supabase
      .from('notification_banner')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    setBanner(data && stillRunning(data) ? data : null);
  };
  
  useEffect(() => {
    const updateOffset = () => {
      const active = !!(banner && banner.is_active && !isDismissed);
      const h = active && bannerRef.current ? bannerRef.current.offsetHeight : 0;
      document.documentElement.style.setProperty('--banner-offset', `${h}px`);
    };
    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => {
      window.removeEventListener('resize', updateOffset);
      if (!banner || !banner.is_active || isDismissed) {
        document.documentElement.style.setProperty('--banner-offset', '0px');
      }
    };
  }, [banner, isDismissed]);
  
  if (!banner || !banner.is_active || isDismissed || !stillRunning(banner)) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="w-full py-2 overflow-hidden relative"
      style={{
        backgroundColor: banner.background_color,
        color: banner.text_color
      }}
    >
      <div className="animate-scroll-left whitespace-nowrap text-base font-medium inline-flex">
        <span className="px-10">{banner.message}</span>
        <span className="px-10">{banner.message}</span>
        <span className="px-10">{banner.message}</span>
        <span className="px-10">{banner.message}</span>
        <span className="px-10">{banner.message}</span>
        <span className="px-10">{banner.message}</span>
      </div>
      <button
        onClick={() => setIsDismissed(true)}
        className="absolute right-4 top-1/2 -translate-y-1/2 hover:opacity-80 transition-opacity bg-black rounded-full p-1 z-10"
        aria-label="Dismiss notification"
      >
        <X className="h-3 w-3 text-white" />
      </button>
      
      <style>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        
        .animate-scroll-left {
          animation: scroll-left 24s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default NotificationBanner;
